const DEFAULT_PASSWORD_EXPIRATION_DAYS = 180;
const MIN_PASSWORD_EXPIRATION_DAYS = 30;
const MAX_PASSWORD_EXPIRATION_DAYS = 999;

const PASSWORD_EXPIRATION_DAYS_INVALID_KEY =
  'Accounts.Security.PasswordExpirationDaysInvalid';

const USER_PASSWORD_RESET_ON_STORAGE_KEY = 'userPasswordResetOn';

const isUnsetAccountValue = (raw) =>
  [undefined, 'undefined', null, 'null', ''].includes(raw);

const isWithinRange = (days) =>
  days >= MIN_PASSWORD_EXPIRATION_DAYS && days <= MAX_PASSWORD_EXPIRATION_DAYS;

const parseStrictWholeNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || !/^\d+$/.test(trimmed)) {
      return null;
    }
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isSafeInteger(parsed) ? parsed : null;
  }
  return null;
};

const validatePasswordExpirationDaysForSave = (value) => {
  if (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return { valid: true, days: null };
  }
  const days = parseStrictWholeNumber(value);
  if (days === null || !isWithinRange(days)) {
    return { valid: false, errorKey: PASSWORD_EXPIRATION_DAYS_INVALID_KEY };
  }
  return { valid: true, days };
};

const parsePasswordExpirationDaysFromAccount = (account) => {
  const raw = account && account.password_expiration_days;
  if (isUnsetAccountValue(raw)) {
    return null;
  }
  const days = parseStrictWholeNumber(raw);
  if (days === null || !isWithinRange(days)) {
    return null;
  }
  return days;
};

const formatPasswordExpirationDaysForSave = (days) =>
  days === null || days === undefined ? '' : String(days);

const resolvePasswordExpirationDaysFromApi = (apiValue) => {
  if (isUnsetAccountValue(apiValue)) {
    return DEFAULT_PASSWORD_EXPIRATION_DAYS;
  }
  const days = parseStrictWholeNumber(apiValue);
  if (days === null || !isWithinRange(days)) {
    return DEFAULT_PASSWORD_EXPIRATION_DAYS;
  }
  return days;
};

const computePasswordExpirationTimestamp = (
  passwordResetOn,
  expirationDays,
  now = Date.now()
) => {
  const resetDate = parsePasswordResetOnDate(passwordResetOn);
  if (!resetDate) {
    return null;
  }
  const expiresAt = new Date(resetDate);
  expiresAt.setDate(expiresAt.getDate() + expirationDays);
  return expiresAt.getTime();
};

const isPortalPasswordExpired = (
  passwordResetOn,
  expirationDays,
  now = Date.now()
) => {
  const expiresAtMs = computePasswordExpirationTimestamp(
    passwordResetOn,
    expirationDays,
    now
  );
  if (expiresAtMs === null) {
    return false;
  }
  return expiresAtMs <= now;
};

const parsePasswordResetOnDate = (value) => {
  if (isUnsetAccountValue(value)) {
    return null;
  }
  const resetDate = new Date(value);
  if (Number.isNaN(resetDate.getTime())) {
    return null;
  }
  return resetDate;
};

const persistUserPasswordResetOn = (passwordResetOn) => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  const resetDate = parsePasswordResetOnDate(passwordResetOn);
  if (!resetDate) {
    sessionStorage.removeItem(USER_PASSWORD_RESET_ON_STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(
    USER_PASSWORD_RESET_ON_STORAGE_KEY,
    resetDate.toISOString()
  );
};

const persistUserPasswordResetOnFromUser = (user) => {
  if (!user || typeof user !== 'object') {
    return;
  }
  persistUserPasswordResetOn(user.password_reset_on);
};

const formatPasswordResetOnDdMmYyyy = (passwordResetOn) => {
  const resetDate = parsePasswordResetOnDate(passwordResetOn);
  if (!resetDate) {
    return null;
  }
  const day = String(resetDate.getDate()).padStart(2, '0');
  const month = String(resetDate.getMonth() + 1).padStart(2, '0');
  const year = resetDate.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatPasswordResetOnForDisplay = (passwordResetOn, _locale) =>
  formatPasswordResetOnDdMmYyyy(passwordResetOn);

const getStoredUserPasswordResetOn = () => {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(USER_PASSWORD_RESET_ON_STORAGE_KEY);
};

const getPasswordLastUpdatedMessage = (t, _locale) => {
  const formatted = formatPasswordResetOnDdMmYyyy(getStoredUserPasswordResetOn());
  if (!formatted) {
    return t('Accounts.Security.PasswordLastUpdatedUnavailable');
  }
  return t('Accounts.Security.PasswordLastUpdated', { date: formatted });
};

module.exports = {
  DEFAULT_PASSWORD_EXPIRATION_DAYS,
  MIN_PASSWORD_EXPIRATION_DAYS,
  MAX_PASSWORD_EXPIRATION_DAYS,
  PASSWORD_EXPIRATION_DAYS_INVALID_KEY,
  validatePasswordExpirationDaysForSave,
  parsePasswordExpirationDaysFromAccount,
  formatPasswordExpirationDaysForSave,
  resolvePasswordExpirationDaysFromApi,
  computePasswordExpirationTimestamp,
  isPortalPasswordExpired,
  USER_PASSWORD_RESET_ON_STORAGE_KEY,
  persistUserPasswordResetOn,
  persistUserPasswordResetOnFromUser,
  formatPasswordResetOnForDisplay,
  formatPasswordResetOnDdMmYyyy,
  getStoredUserPasswordResetOn,
  getPasswordLastUpdatedMessage,
};



// WEBPACK FOOTER //
// ./src/utils/passwordExpiration.js